// PricingView.jsx
//
// One settings page split into three top-level tabs so the user never
// faces a wall of stacked content:
//
//   Prices           → a guided, visual rate card: what each action
//                      costs, how billing works, real worked examples,
//                      image models and knowledge-base pricing
//   Build your agent → assemble an agent and see the monthly cost + the
//                      plan that fits, computed live (see AgentBuilder.jsx)
//   FAQ              → short answers about coins, billing and usage
//
// Backend wire-up: GET /pricing/catalog via usePricingCatalog (public).
import React, { useEffect, useMemo, useState } from 'react';
import {
  Headset,
  Workflow,
  Plug,
  Database,
  Image as ImageIcon,
  BookOpen,
  ChevronRight,
  Coins,
  X,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  MessageCircle,
  Zap,
  CheckCircle2,
  Sparkles,
  Shield,
  Calculator,
  ArrowRight,
  UserPlus,
} from 'lucide-react';
import usePricingCatalog from '../../hooks/billing/usePricingCatalog';
import AgentBuilder from './components/AgentBuilder';
import './PricingView.css';

const PAGE_TABS = [
  { id: 'prices', label: 'Prices' },
  { id: 'builder', label: 'Build your agent' },
  { id: 'faq', label: 'FAQ' },
];

// ---------------------------------------------------------------------------
// PricingView — top-level container with page-level tabs
// ---------------------------------------------------------------------------

export default function PricingView() {
  const [pageTab, setPageTab] = useState('prices');
  const { data: catalog, loading: catalogLoading, error: catalogError } =
    usePricingCatalog();

  if (catalogError) {
    return (
      <div className="pricing-page">
        <div className="pricing-header">
          <h1>Pricing</h1>
          <p>Something went wrong loading the catalog. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pricing-page">
      <div className="pricing-header">
        <h1>Pricing made simple</h1>
        <p>Understand what every Zenit action costs — in seconds.</p>
      </div>

      <div className="pricing-page-tabs" role="tablist">
        {PAGE_TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={pageTab === t.id}
            className={`pricing-page-tab ${pageTab === t.id ? 'active' : ''}`}
            onClick={() => setPageTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="pricing-page-body">
        {pageTab === 'prices' && (
          <PricingResources
            catalog={catalog}
            loading={catalogLoading}
            onOpenBuilder={() => setPageTab('builder')}
          />
        )}
        {pageTab === 'builder' && (
          <AgentBuilder catalog={catalog} loading={catalogLoading} />
        )}
        {pageTab === 'faq' && <PricingFAQ catalog={catalog} />}
      </div>
    </div>
  );
}

// ===========================================================================
// Prices tab — a guided visual rate card
// ===========================================================================

function PricingResources({ catalog, loading, onOpenBuilder }) {
  const [showAgents, setShowAgents] = useState(false);

  if (loading || !catalog) {
    return <div className="pricing-skeleton">Loading prices…</div>;
  }
  const { resources, assistantTiers, imageModels, comingSoon } = catalog;

  const agentTiers = (assistantTiers ?? []).map((tier) => ({
    tier: tier.tier,
    label: TIER_LABEL_EN[tier.tier] ?? tier.label,
    subtitle: TIER_SUBTITLE_EN[tier.tier],
    models: tier.models ?? [],
    coins: tier.coins,
  }));

  // Live catalog figures — headline numbers never drift from the detail.
  const cheapestAgent = agentTiers.reduce(
    (min, t) => (t.coins != null && (min == null || t.coins < min) ? t.coins : min),
    null,
  );
  const sortedImages = [...(imageModels ?? [])].sort(
    (a, b) => (a.coinsPerImage ?? Infinity) - (b.coinsPerImage ?? Infinity),
  );
  const cheapestImage = sortedImages[0]?.coinsPerImage ?? null;
  const cheapestImageModel = sortedImages[0] ?? null;

  const wfCoins = resources.workflow_execution?.coins ?? 1;
  const integCoins = resources.integration_call?.coins ?? 1;
  const dbReadCoins = resources.database_read?.coins ?? 0;
  const dbWriteCoins = resources.database_write?.coins ?? 1;
  const agentBase = cheapestAgent ?? 1;

  // Worked examples, computed from the live rate card so the totals can
  // never contradict the numbers shown above.
  const examples = [
    {
      id: 'whatsapp',
      icon: <MessageCircle size={18} />,
      accent: 'green',
      title: 'Reply on WhatsApp',
      lines: [
        { icon: <Workflow size={13} />, label: '1 workflow', coins: wfCoins },
        { icon: <Headset size={13} />, label: '1 agent', coins: agentBase },
        { icon: <Plug size={13} />, label: '1 integration', coins: integCoins },
      ],
    },
    {
      id: 'lead',
      icon: <UserPlus size={18} />,
      accent: 'amber',
      title: 'Save a lead',
      lines: [
        { icon: <Workflow size={13} />, label: '1 workflow', coins: wfCoins },
        { icon: <Plug size={13} />, label: '1 integration', coins: integCoins },
        { icon: <Database size={13} />, label: '1 DB write', coins: dbWriteCoins },
      ],
    },
    {
      id: 'image',
      icon: <ImageIcon size={18} />,
      accent: 'pink',
      title: 'Generate an image',
      lines: [
        {
          icon: <ImageIcon size={13} />,
          label: cheapestImageModel?.displayName ?? 'Image model',
          coins: cheapestImage ?? 0,
        },
      ],
    },
  ].map((ex) => ({
    ...ex,
    total: ex.lines.reduce((sum, l) => sum + (l.coins ?? 0), 0),
    approx: ex.lines.length > 1,
  }));

  // Drop RAG from the catalog's coming-soon list — we now publish its
  // real rate card below (values sourced from PricingSettings defaults).
  const otherComingSoon = (comingSoon ?? []).filter((f) => f.feature !== 'rag');

  return (
    <div className="pricing-rate">
      {/* ---- §1 What each action costs ---- */}
      <section className="pricing-block">
        <SectionHeading n={1} title="What each action costs" />

        <div className="hero-tiles">
          <HeroTile
            icon={<Headset size={22} />}
            variant="violet"
            title="Agents"
            prefix={cheapestAgent != null ? 'From' : null}
            value={cheapestAgent ?? '—'}
            unit="coin / call"
            action="View tiers"
            onClick={() => setShowAgents(true)}
          />
          <HeroTile
            icon={<Workflow size={22} />}
            variant="indigo"
            title="Workflows"
            value={wfCoins}
            unit="coin / execution"
            pill="Steps are free"
          />
          <HeroTile
            icon={<Plug size={22} />}
            variant="blue"
            title="Integrations"
            value={integCoins}
            unit="coin / action"
          />
          <HeroTile
            icon={<ImageIcon size={22} />}
            variant="pink"
            title="Images"
            prefix={cheapestImage != null ? 'From' : null}
            value={cheapestImage ?? '—'}
            unit="coins / image"
          />
        </div>

        <RateBar
          icon={<Database size={17} />}
          title="Database"
          items={[
            { label: 'Read', coins: dbReadCoins },
            { label: 'Write', coins: dbWriteCoins },
          ]}
        />

        <KnowledgeBaseCard prices={KNOWLEDGE_BASE_PRICES} />

        {otherComingSoon.map((feature) => (
          <RateBar
            key={feature.feature}
            icon={<BookOpen size={17} />}
            title={feature.displayName}
            badge="Coming soon"
            note={feature.description}
          />
        ))}
      </section>

      {/* ---- §2 How it's calculated ---- */}
      <section className="pricing-block">
        <SectionHeading n={2} title="How it's calculated" />
        <HowItWorks />
      </section>

      {/* ---- §3 Real examples ---- */}
      <section className="pricing-block">
        <SectionHeading
          n={3}
          title="Real examples"
          hint="Computed from the live rate card above."
        />
        <RealExamples examples={examples} />
      </section>

      {/* ---- §4 Image models ---- */}
      {sortedImages.length > 0 && (
        <section className="pricing-block">
          <SectionHeading n={4} title="Image models" />
          <ImageModelsList models={sortedImages} />
        </section>
      )}

      {/* ---- §5 Total peace of mind ---- */}
      <section className="pricing-block">
        <SectionHeading n={5} title="Total peace of mind" />
        <TrustSection onOpenBuilder={onOpenBuilder} />
      </section>

      {showAgents && (
        <AgentsModal tiers={agentTiers} onClose={() => setShowAgents(false)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section heading — numbered chip + title
// ---------------------------------------------------------------------------

function SectionHeading({ n, title, hint }) {
  return (
    <div className="pricing-block-head">
      <span className="pricing-block-num">{n}</span>
      <h2 className="pricing-block-title">{title}</h2>
      {hint && <span className="pricing-block-hint">{hint}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hero tile — one action, one headline number
// ---------------------------------------------------------------------------

function HeroTile({
  icon,
  variant,
  title,
  prefix,
  value,
  unit,
  pill,
  action,
  onClick,
}) {
  const clickable = typeof onClick === 'function';
  const Tag = clickable ? 'button' : 'div';
  return (
    <Tag
      type={clickable ? 'button' : undefined}
      className={`hero-tile hero-tile--${variant} ${
        clickable ? 'hero-tile--clickable' : ''
      }`}
      onClick={onClick}
    >
      <span className="hero-tile-icon">{icon}</span>
      <span className="hero-tile-title">{title}</span>
      <div className="hero-tile-value">
        {prefix && <span className="hero-tile-prefix">{prefix}</span>}
        <span className="hero-tile-number">
          {typeof value === 'number' ? value.toLocaleString('en-US') : value}
        </span>
      </div>
      <span className="hero-tile-unit">{unit}</span>
      {pill && <span className="hero-tile-pill">{pill}</span>}
      {action && (
        <span className="hero-tile-action">
          {action} <ChevronRight size={13} />
        </span>
      )}
    </Tag>
  );
}

// ---------------------------------------------------------------------------
// Rate bar — a slim full-width strip for simple two-value resources
// ---------------------------------------------------------------------------

function RateBar({ icon, title, items, badge, note }) {
  return (
    <div className="rate-bar">
      <span className="rate-bar-title">
        <span className="rate-bar-icon">{icon}</span>
        {title}
        {badge && <span className="pricing-badge">{badge}</span>}
      </span>
      {note && <span className="rate-bar-note">{note}</span>}
      {items && (
        <span className="rate-bar-items">
          {items.map((it, idx) => (
            <React.Fragment key={it.label}>
              {idx > 0 && <span className="rate-bar-sep" aria-hidden>·</span>}
              <span className="rate-bar-item">
                <span className="rate-bar-label">{it.label}</span>
                <CoinValue coins={it.coins} />
              </span>
            </React.Fragment>
          ))}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Knowledge base card — richer rate card (values from PricingSettings)
// ---------------------------------------------------------------------------

function KnowledgeBaseCard({ prices }) {
  const rows = [
    { label: 'Create & manage', sub: 'Spin up a knowledge base', coins: prices.create },
    { label: 'Query', sub: 'Per retrieval your agent runs', coins: prices.query },
    {
      label: 'Indexing',
      sub: 'When you add documents',
      range: [prices.indexSmall, prices.indexLarge],
      rangeSub: 'small → large',
    },
    {
      label: 'Storage',
      sub: 'Only over your included quota',
      coins: prices.storageOverage,
      perUnit: '/ MB',
    },
  ];
  return (
    <div className="kb-card">
      <div className="kb-card-head">
        <span className="rate-bar-icon">
          <BookOpen size={17} />
        </span>
        <span className="kb-card-title">Knowledge base</span>
        <span className="pricing-badge pricing-badge--new">New</span>
      </div>
      <div className="kb-card-rows">
        {rows.map((r) => (
          <div key={r.label} className="kb-row">
            <div className="kb-row-left">
              <span className="kb-row-label">{r.label}</span>
              {r.sub && <span className="kb-row-sub">{r.sub}</span>}
            </div>
            <div className="kb-row-right">
              {r.range ? (
                <span className="kb-range">
                  <span className="kb-range-value">{r.range[0]}</span>
                  <span className="kb-range-dash">–</span>
                  <span className="kb-range-value">{r.range[1]}</span>
                  <span className="kb-range-unit">
                    coins{r.rangeSub ? ` · ${r.rangeSub}` : ''}
                  </span>
                </span>
              ) : (
                <CoinValue coins={r.coins} perUnit={r.perUnit} />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// A single "N coins" / "Free" value renderer, shared across the rate card.
function CoinValue({ coins, perUnit }) {
  if (coins == null) return <span className="coin-value coin-value--dash">—</span>;
  if (coins === 0) return <span className="coin-value coin-value--free">Free</span>;
  return (
    <span className="coin-value">
      <span className="coin-value-num">{coins.toLocaleString('en-US')}</span>
      <span className="coin-value-unit">
        {coins === 1 ? 'coin' : 'coins'}
        {perUnit ? ` ${perUnit}` : ''}
      </span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// How it works — Event → Workflow → Agent → Integration → Result
// ---------------------------------------------------------------------------

// All nodes share the Workflows tile colour so the flow reads as one
// calm indigo band instead of a rainbow of gradients.
const FLOW_STEPS = [
  { icon: <Zap size={20} />, label: 'Event', sub: 'Something happens' },
  { icon: <Workflow size={20} />, label: 'Workflow', sub: 'Orchestrates steps' },
  { icon: <Headset size={20} />, label: 'Agent', sub: 'Makes decisions' },
  { icon: <Plug size={20} />, label: 'Integration', sub: 'Runs actions' },
  { icon: <CheckCircle2 size={20} />, label: 'Result', sub: 'Task completed' },
];

function HowItWorks() {
  return (
    <div className="pricing-howto">
      <div className="flow-track">
        {FLOW_STEPS.map((s, idx) => (
          <React.Fragment key={s.label}>
            <div className="flow-node">
              <span className="flow-node-icon">{s.icon}</span>
              <span className="flow-node-label">{s.label}</span>
              <span className="flow-node-sub">{s.sub}</span>
            </div>
            {idx < FLOW_STEPS.length - 1 && (
              <span className="flow-arrow" aria-hidden>
                <ArrowRight size={16} />
              </span>
            )}
          </React.Fragment>
        ))}
      </div>
      <div className="flow-tagline">
        <Sparkles size={15} />
        You only pay for the actions that run.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Real examples — worked totals from the live rate card
// ---------------------------------------------------------------------------

function RealExamples({ examples }) {
  return (
    <div className="example-grid">
      {examples.map((ex) => (
        <div key={ex.id} className="example-card">
          <div className="example-head">
            <span className={`example-icon example-icon--${ex.accent}`}>
              {ex.icon}
            </span>
            <span className="example-title">{ex.title}</span>
          </div>
          <div className="example-lines">
            {ex.lines.map((line, idx) => (
              <div key={idx} className="example-line">
                <span className="example-line-label">
                  <span className="example-line-icon">{line.icon}</span>
                  {line.label}
                </span>
                <CoinValue coins={line.coins} />
              </div>
            ))}
          </div>
          <div className={`example-total example-total--${ex.accent}`}>
            <span className="example-total-eq">=</span>
            <span className="example-total-num">
              {ex.total.toLocaleString('en-US')}
            </span>
            <span className="example-total-unit">
              {ex.approx ? 'coins approx.' : 'coins'}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Image models — full list with per-image coin cost
// ---------------------------------------------------------------------------

function ImageModelsList({ models }) {
  return (
    <div className="image-models">
      {models.map((m, idx) => (
        <div key={m.modelKey} className="image-model-row">
          <span className="image-model-name">
            <span className="image-model-icon">
              <ImageIcon size={15} />
            </span>
            {m.displayName}
            {idx === 0 && <span className="image-model-tag">Cheapest</span>}
          </span>
          <span className="image-model-price">
            <span className="image-model-coins">
              {m.coinsPerImage != null
                ? m.coinsPerImage.toLocaleString('en-US')
                : '—'}
            </span>
            <span className="image-model-unit">coins / image</span>
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trust section — reassurance badges + calculator CTA
// ---------------------------------------------------------------------------

function TrustSection({ onOpenBuilder }) {
  const badges = [
    { icon: <Shield size={16} />, label: 'No hidden costs' },
    { icon: <Coins size={16} />, label: 'Pay per use' },
    { icon: <Calculator size={16} />, label: 'Estimate before you run' },
  ];
  return (
    <div className="trust-strip">
      <div className="trust-badges">
        {badges.map((b) => (
          <div key={b.label} className="trust-badge">
            <span className="trust-badge-icon">{b.icon}</span>
            {b.label}
          </div>
        ))}
      </div>
      <button
        type="button"
        className="trust-cta"
        onClick={onOpenBuilder}
      >
        <Calculator size={16} />
        Build your agent
        <ArrowRight size={15} />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Agents modal — all tiers, each expandable to its model directory
// ---------------------------------------------------------------------------

function AgentsModal({ tiers, onClose }) {
  const [openTier, setOpenTier] = useState(tiers?.[0]?.tier ?? null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="tier-modal-overlay"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="tier-modal" onClick={(e) => e.stopPropagation()}>
        <button className="tier-modal-close" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>
        <div className="agents-modal-head">
          <h3 className="tier-modal-title">Agent pricing</h3>
          <p className="tier-modal-subtitle">
            Billed per call by model tier — pick the tier that fits the task.
          </p>
        </div>
        <div className="agents-modal-tiers">
          {tiers.map((t) => (
            <AgentTierRow
              key={t.tier}
              tier={t}
              open={openTier === t.tier}
              onToggle={() =>
                setOpenTier((cur) => (cur === t.tier ? null : t.tier))
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function AgentTierRow({ tier, open, onToggle }) {
  const hasModels = tier.models && tier.models.length > 0;

  const groups = useMemo(() => {
    const out = new Map();
    for (const m of tier.models ?? []) {
      const { provider, model } = parseProviderModel(m);
      const key = provider || 'Other';
      if (!out.has(key)) out.set(key, []);
      out.get(key).push(model);
    }
    return Array.from(out.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [tier.models]);

  return (
    <div className={`agents-tier ${open ? 'agents-tier--open' : ''}`}>
      <button
        type="button"
        className="agents-tier-head"
        onClick={() => hasModels && onToggle()}
        disabled={!hasModels}
      >
        <span className="agents-tier-left">
          <span className="agents-tier-label">{tier.label}</span>
          {tier.subtitle && (
            <span className="agents-tier-sub">{tier.subtitle}</span>
          )}
        </span>
        <span className="agents-tier-right">
          <span className="agents-tier-coins">{tier.coins}</span>
          <span className="agents-tier-unit">coins / call</span>
          {hasModels && (
            <span className="agents-tier-chevron" aria-hidden>
              <ChevronRight size={15} />
            </span>
          )}
        </span>
      </button>
      {open && hasModels && (
        <div className="agents-tier-body">
          {groups.map(([provider, models]) => (
            <div key={provider} className="tier-modal-group">
              <div className="tier-modal-group-header">
                <span className={`provider-badge provider-${provider}`}>
                  {PROVIDER_LABEL[provider] ?? provider}
                </span>
                <span className="tier-modal-group-count">
                  {models.length} {models.length === 1 ? 'model' : 'models'}
                </span>
              </div>
              <div className="tier-modal-group-models">
                {models.map((m) => (
                  <div key={m} className="model-card">
                    <span className="model-card-name">{m}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function parseProviderModel(id) {
  const slash = id.indexOf('/');
  if (slash === -1) return { provider: '', model: id };
  return { provider: id.slice(0, slash), model: id.slice(slash + 1) };
}

// Provider slug → display label. Fallback is the raw slug.
const PROVIDER_LABEL = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  google: 'Google',
  deepseek: 'DeepSeek',
  meta: 'Meta',
  mistral: 'Mistral',
  xai: 'xAI',
};

// Knowledge base (RAG) coin prices. Sourced from the backend
// PricingSettings defaults (shared_core config.py): KB_OPERATION,
// RAG_RETRIEVAL, RAG_INGEST_S/M/L and RAG_STORAGE_OVERAGE_PER_MB. The
// public /pricing/catalog still lists RAG under `comingSoon`, so we
// publish the rate card here directly until the catalog exposes it.
const KNOWLEDGE_BASE_PRICES = {
  create: 0, // KB_OPERATION — create/manage a knowledge base
  query: 2, // RAG_RETRIEVAL — per retrieval
  indexSmall: 5, // RAG_INGEST_S
  indexMedium: 15, // RAG_INGEST_M
  indexLarge: 50, // RAG_INGEST_L
  storageOverage: 1, // RAG_STORAGE_OVERAGE — per MB over quota
};

// ---------------------------------------------------------------------------
// FAQ tab
// ---------------------------------------------------------------------------

const FAQ_ITEMS = [
  {
    q: 'How are Zenit Coins calculated?',
    a: 'Each task is priced by the actions it actually uses — agent calls, workflow runs, integrations, database writes and image generation. You only pay for what runs, never for idle capacity.',
  },
  {
    q: 'What counts as one task execution?',
    a: 'One completed job — for example replying to a message, updating an order, or classifying an email. What a task costs depends on the actions it performs along the way.',
  },
  {
    q: 'How do I get coins?',
    a: 'Every plan includes a monthly coin allowance — from 100 on Free up to 5,000 on Professional. Need more? Upgrade your plan, or contact contact@zenitailab.com to top up — you can also request it from the Wallet’s “Get more coins”.',
  },
  {
    q: 'Do subscriptions renew monthly?',
    a: 'Yes. Paid plans renew automatically each billing cycle — monthly, or yearly if you chose annual billing — and your coin allowance is refreshed on every renewal.',
  },
  {
    q: 'Do unused coins roll over?',
    a: 'Your plan’s monthly coins reset at each renewal, so unused plan coins don’t roll over. Extra coins you add on top of your plan are different — they never expire and stay in your balance until you use them.',
  },
  {
    q: 'What happens if I run out of coins?',
    a: 'Coin-billed actions pause until you add more coins or your plan renews on its next cycle, so you’re never charged beyond your balance. Free actions like database reads keep working.',
  },
  {
    q: 'Do workflow steps cost extra?',
    a: 'No. Only the workflow execution is billed — its individual steps are free. Any integrations or database writes a step performs are billed separately, at their normal rate.',
  },
  {
    q: 'Are integrations charged separately?',
    a: 'Yes. Each integration call — WhatsApp, Shopify, Google, Meta and others — costs 1 coin, on top of the agent or workflow that triggered it.',
  },
  {
    q: 'Is database reading free?',
    a: 'Yes. Reads are free — they’re audit-only. Only writes cost coins, at 1 coin per write.',
  },
  {
    q: 'Can I estimate the cost before launch?',
    a: 'Absolutely. Open “Build your agent”, assemble your setup and pick a monthly volume — you’ll instantly see the cost per task, the monthly total, and which plan fits.',
  },
];

function PricingFAQ({ catalog }) {
  // First two open by default, mirroring the featured layout.
  const [open, setOpen] = useState(() => new Set([0, 1]));
  const toggle = (i) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  const examples = buildPlainExamples(catalog);

  return (
    <div className="faq2">
      <div className="faq2-head">
        <span className="faq2-head-icon">
          <HelpCircle size={20} />
        </span>
        <div className="faq2-head-text">
          <h2>Popular questions</h2>
          <p>Everything you need to know about pricing and usage.</p>
        </div>
      </div>

      <div className="faq2-grid">
        {FAQ_ITEMS.map((item, i) => {
          const isOpen = open.has(i);
          return (
            <div
              key={i}
              className={`faq2-item ${isOpen ? 'faq2-item--open' : ''}`}
            >
              <button
                type="button"
                className="faq2-q"
                onClick={() => toggle(i)}
                aria-expanded={isOpen}
              >
                <span
                  className={`faq2-num ${
                    i % 2 ? 'faq2-num--blue' : 'faq2-num--violet'
                  }`}
                >
                  {i + 1}
                </span>
                <span className="faq2-q-text">{item.q}</span>
                <span className="faq2-chev" aria-hidden>
                  {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </span>
              </button>
              {isOpen && (
                <div className="faq2-a">
                  <p>{item.a}</p>
                  <Sparkles size={15} className="faq2-a-spark" aria-hidden />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {examples.length > 0 && (
        <div className="faq2-examples">
          <div className="faq2-head">
            <span className="faq2-head-icon faq2-head-icon--spark">
              <Sparkles size={20} />
            </span>
            <div className="faq2-head-text">
              <h2>Examples in plain English</h2>
              <p>Real use cases and how coins are used.</p>
            </div>
          </div>
          <div className="faq2-ex-list">
            {examples.map((ex) => (
              <div key={ex.id} className="faq2-ex">
                <span className={`faq2-ex-icon faq2-ex-icon--${ex.accent}`}>
                  {ex.icon}
                </span>
                <span className="faq2-ex-text">{ex.text}</span>
                <span className="faq2-ex-coins">
                  <span className="faq2-ex-approx">{ex.approx ? '≈' : '='}</span>
                  <strong>{ex.total.toLocaleString('en-US')}</strong>
                  coins
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Plain-English worked examples for the FAQ, computed from the live
 * catalog so the coin totals match the Prices tab exactly.
 */
function buildPlainExamples(catalog) {
  if (!catalog) return [];
  const r = catalog.resources ?? {};
  const wf = r.workflow_execution?.coins ?? 1;
  const integ = r.integration_call?.coins ?? 1;
  const dbWrite = r.database_write?.coins ?? 1;
  const agent =
    [...(catalog.assistantTiers ?? [])].sort(
      (a, b) => (a.coins ?? 0) - (b.coins ?? 0)
    )[0]?.coins ?? 1;
  const image =
    [...(catalog.imageModels ?? [])].sort(
      (a, b) => (a.coinsPerImage ?? Infinity) - (b.coinsPerImage ?? Infinity)
    )[0]?.coinsPerImage ?? 65;

  return [
    {
      id: 'whatsapp',
      accent: 'green',
      icon: <MessageCircle size={16} />,
      text: 'A customer messages your agent on WhatsApp and it replies.',
      total: wf + agent + integ,
      approx: true,
    },
    {
      id: 'lead',
      accent: 'amber',
      icon: <UserPlus size={16} />,
      text: 'A new lead fills out a form and your agent saves it to your database.',
      total: wf + integ + dbWrite,
      approx: true,
    },
    {
      id: 'image',
      accent: 'pink',
      icon: <ImageIcon size={16} />,
      text: 'Your agent generates a product image on request.',
      total: image,
      approx: false,
    },
  ];
}

// ---------------------------------------------------------------------------
// Static i18n shims for backend strings (until the lambda ships English)
// ---------------------------------------------------------------------------

// Tier int → label shown by the FE picker.
const TIER_LABEL_EN = {
  1: 'Budget',
  3: 'Standard',
  4: 'Premium',
  5: 'Top',
};

// Tier int → short capability descriptor under the tier name.
const TIER_SUBTITLE_EN = {
  1: 'Simple tasks',
  3: 'Balanced',
  4: 'Advanced reasoning',
  5: 'Highest capability',
};

